import * as React from "react";

import * as store2 from "store2";
import { StateKey, TOTAL_MODULES } from "~/constants";

interface GlobalUserStateContext {
  firstName: string;
  lastName: string;
  email: string;
  state: StateKey | null;

  view: "start" | "registration" | "module" | "end";
  startedModules: Record<number, true>;
  completedModules: Record<number, true>;
  usersModuleAnswers: Record<number, Record<number, number[]>>;
  currentModule: number;
  currentQuestion: number; // todo : currentQuestion doesn't seem to have any effect even on pre hwwa website (currentQuestion isn't restored on load)

  dateLastUsed: number;
}

const GlobalUserStateContext = React.createContext<
  GlobalUserStateContext | undefined
>(undefined);

const GlobalUserDispatchContext = React.createContext<
  | ((
      value: React.SetStateAction<GlobalUserStateContext>,
      saveToStore?: any,
    ) => void)
  | undefined
>(undefined);

interface GlobalUserMethodsContext {
  goToRegistration: () => void;
  goToModule: (module: number) => void;
  nextModule: () => void;
  reset: () => void;
  finish: () => void;
}

const GlobalUserMethodsContext = React.createContext<
  GlobalUserMethodsContext | undefined
>(undefined);

const STORAGE_KEY = "user";

const DEFAULT_STATE: GlobalUserStateContext = {
  email: "",
  lastName: "",
  firstName: "",
  state: null,
  view: "start",
  startedModules: {},
  completedModules: {},
  usersModuleAnswers: {},
  currentModule: 0,
  currentQuestion: 0,
  dateLastUsed: Date.now(),
};

export const GlobalUserProvider: React.FC<{}> = ({ children }) => {
  const [state, $setState] = React.useState<GlobalUserStateContext>(() => {
    if (store2.has(STORAGE_KEY)) {
      return store2.get(STORAGE_KEY);
    }

    return DEFAULT_STATE;
  });

  const stateRef = React.useRef<GlobalUserStateContext>(state);

  React.useEffect(() => {
    stateRef.current = state;
  });

  const getState = React.useCallback(() => {
    return stateRef.current;
  }, [stateRef]);

  const setState = React.useCallback(
    (
      value: React.SetStateAction<GlobalUserStateContext>,
      saveToStore = true,
    ) => {
      const nextState = typeof value === "function" ? value(getState()) : value;
      $setState(nextState);

      if (saveToStore) {
        store2.set(STORAGE_KEY, nextState);
      }
    },
    [getState],
  );

  const reset = React.useCallback(() => {
    return setState(DEFAULT_STATE);
  }, []);

  const goToRegistration = React.useCallback((saveToStore: boolean = false) => {
    setState(
      (prev) => ({
        ...prev,
        view: "registration",
        dateLastUsed: Date.now(),
      }),
      saveToStore,
    );
  }, []);

  const goToModule = React.useCallback(
    (module: number) => {
      const nextLastUsed = Date.now();

      return setState((prev) => ({
        ...prev,
        view: "module",
        currentModule: module,
        currentQuestion: 0,
        dateLastUsed: nextLastUsed,
        startedModules: {
          ...prev.startedModules,
          [module]: true,
        },
      }));
    },
    [setState],
  );

  const nextModule = React.useCallback(() => {
    const curState = getState();

    const nextState: GlobalUserStateContext = {
      ...curState,
      startedModules: {
        ...curState.startedModules,
        [curState.currentModule + 1]: true,
      },
      completedModules: {
        ...curState.completedModules,
        [curState.currentModule]: true,
      },
      view: "module",
      currentModule: curState.currentModule + 1,
      currentQuestion: 0,
      dateLastUsed: Date.now(),
    };

    setState(nextState);
  }, [getState, setState]);

  const finish = React.useCallback(() => {
    const curState = getState();

    const nextState: GlobalUserStateContext = {
      ...curState,
      completedModules: (() => {
        const dict = Array.from(
          { length: TOTAL_MODULES },
          (v, k) => k + 1,
        ).reduce((acc, cur) => {
          acc[cur] = true;
          return acc;
        }, {} as Record<number, true>);

        return dict;
      })(),
      view: "end",
      currentModule: 0,
      currentQuestion: 0,
      dateLastUsed: Date.now(),
    };

    setState(nextState);
  }, [setState]);

  return (
    <GlobalUserStateContext.Provider value={state}>
      <GlobalUserDispatchContext.Provider value={setState}>
        <GlobalUserMethodsContext.Provider
          value={{
            goToRegistration,
            goToModule,
            nextModule,
            finish,
            reset,
          }}
        >
          {children}
        </GlobalUserMethodsContext.Provider>
      </GlobalUserDispatchContext.Provider>
    </GlobalUserStateContext.Provider>
  );
};

export const useGlobalUserState = () => {
  const ctx = React.useContext(GlobalUserStateContext);

  if (!ctx) {
    throw Error("useGlobalUserState used outsite <GlobalUserProvider />");
  }

  return ctx;
};

export const useGlobalUserDispatch = () => {
  const setState = React.useContext(GlobalUserDispatchContext);

  if (!setState) {
    throw Error("useGlobalUserDispatch used outsite <GlobalUserProvider />");
  }

  return setState;
};

export const useGlobalUserMethods = () => {
  const methods = React.useContext(GlobalUserMethodsContext);

  if (!methods) {
    throw Error("useGlobalUserMethods used outsite <GlobalUserProvider />");
  }

  return methods;
};

export const useGlobalUser = () => {
  const state = useGlobalUserState();
  const setState = useGlobalUserDispatch();
  const methods = useGlobalUserMethods();

  return [state, setState, methods] as const;
};
